import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { isEqual, sortBy } from "lodash";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetGlobalLineVocabularyQuery } from "store/queries/benefitLineVocabulary";
import {
  useExplainBenefitSelectionDraftMutation,
  useGetBenefitSelectionExplainQuery,
  useGetBenefitSelectionHistoryQuery,
  useGetBenefitSelectionQuery,
  usePreviewBenefitSelectionMutation,
  useSaveBenefitSelectionMutation,
  useSetBenefitEngineEnabledMutation,
} from "store/queries/benefitSelection";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import styled from "styled-components";
import { IBenefitSelectionRule } from "types/Account/Account";
import {
  BenefitServiceLine,
  BenefitTierPreference,
  IBenefitLineSelection,
  IBenefitPreviewResult,
  IBenefitSelectionExplanation,
} from "types/BenefitSelection/BenefitSelection";

import { BenefitSelectionRules } from "../Eligibility/BenefitSelectionRules";
import { CONFIGURABLE_BENEFITS, serviceLineLabel } from "./constants";
import { EngineStatusLine } from "./EngineStatusLine";
import { ExceptionsList } from "./ExceptionsList";
import { ExplainDrawer } from "./ExplainDrawer";
import { HistoryDrawer } from "./HistoryDrawer";
import { LineSelectionDialog } from "./LineSelectionDialog";
import { LineSelectionTable } from "./LineSelectionTable";
import {
  IBenefitSelectionForm,
  filterStatesSomething,
  toForm,
  toSaveRequest,
} from "./mapper";
import { PhysicianCopayControl } from "./PhysicianCopayControl";
import { PreviewDialog } from "./PreviewDialog";
import { selectionRulesAreValid } from "./SelectionRules/selectionRules";
import { SelectionRulesStep } from "./SelectionRules/SelectionRulesStep";
import { ServiceLinePicker } from "./ServiceLinePicker";
import { Surface } from "./Surface";
import { TierPreferenceControl } from "./TierPreferenceControl";
import { WhatThisAccountReads } from "./WhatThisAccountReads";

/** The card's own name — one step above a step heading, which is one above a section title. */
const CardTitle = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color);
`;

const StepHeading = styled.span`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-color);
`;

/** A layer's sections are far enough apart, and separated enough, to read as separate questions. */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 0;

  & + & {
    border-top: 1px solid var(--surface-200);
  }
`;

/**
 * CLE-2792 — the account's benefit selection configuration, both layers on one card.
 *
 * THE THREE STEPS READ IN CONFIGURING ORDER. 1 — which of the payer's SERVICE LINES this account's
 * copay and co-insurance come from, with payer- and plan-scoped exceptions, because it is the
 * decision the other two are relative to. 2 — the payer filters, which narrow the response before a
 * line is picked. 3 — which LINE inside the service line answers each benefit. They compose rather than merge: "Medicare Advantage reads Physician
 * office" and "for this plan never use HNE NETWORK lines" are different statements with different
 * scopes, and merging them would need a row per combination.
 *
 * 🚨 THE CARD SAVES ITSELF, IN ONE `PUT`, AND THAT INCLUDES THE PAYER FILTERS — and since review
 * round 1 it is the ONLY thing that writes them. They used to sit in the account edit form's field
 * array as well, so "Update Hospital" and this Save both wrote the same rows and the later one won:
 * editing a filter here and then saving the account put the pre-edit list straight back. The filters
 * and the map are read together on every check, so committing one without the other would leave an
 * account reading lines through a filter set nobody chose for the service line now in force.
 *
 * ⚠️ SUPER ADMIN ONLY, matching the API. CLE-2820 removed the narrower per-person gate that sat in
 * front of it during the engine cutover, so visibility and `canEdit` are now the same role check.
 * `canEdit={false}` still renders the whole configuration read-only — nothing passes it today, and
 * it is kept because this decides which care setting an account's patients are quoted from, so a
 * future reader-without-writer is the shape any widening should take.
 */
export function BenefitSelectionCard({ canEdit }: { canEdit: boolean }) {
  const { id: accountId } = useParams<{ id: string }>();
  // `ToastContext` defaults to null, so read it defensively rather than destructuring it.
  const toast = useContext(ToastContext);

  const { data, isLoading, isError } = useGetBenefitSelectionQuery(
    accountId || "",
    { skip: !accountId }
  );
  const { data: vocabulary } = useGetGlobalLineVocabularyQuery(undefined, {
    skip: !accountId,
  });
  const { data: payerByAccount, isLoading: loadingPayers } =
    useGetPayersByAccountQuery(accountId || "", { skip: !accountId });

  const [saveBenefitSelection, { isLoading: saving }] =
    useSaveBenefitSelectionMutation();
  const [setEngineEnabled, { isLoading: switching }] =
    useSetBenefitEngineEnabledMutation();
  const [runPreview, { isLoading: previewing }] =
    usePreviewBenefitSelectionMutation();
  const [explainDraft] = useExplainBenefitSelectionDraftMutation();

  const [form, setForm] = useState<IBenefitSelectionForm | null>(null);
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainBenefit, setExplainBenefit] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<
    IBenefitPreviewResult | undefined
  >(undefined);
  const [previewStatus, setPreviewStatus] = useState<
    "idle" | "unavailable" | "error"
  >("idle");
  const [editingLine, setEditingLine] = useState<string | null>(null);
  const [draftExplanation, setDraftExplanation] = useState<
    IBenefitSelectionExplanation | undefined
  >(undefined);
  const [draftExplainLoading, setDraftExplainLoading] = useState(false);

  const view = data?.data;

  /**
   * 🚨 THE PANEL DESCRIBES THE SCREEN, NOT THE DATABASE, WHENEVER THE TWO DIFFER. Somebody moves the
   * service line, opens "How it is selected" to check what that did, and — before review round 6 —
   * read the steps for the line they had just moved away from, with nothing saying so. The saved
   * explanation is still what is fetched when there is nothing unsaved, because it is cached and
   * costs no request.
   */
  const dirty = useMemo(
    () =>
      Boolean(
        view &&
          form &&
          !isEqual(toSaveRequest(form), toSaveRequest(toForm(view)))
      ),
    [view, form]
  );

  const {
    data: explanation,
    isFetching: savedExplainLoading,
    isError: explainError,
  } = useGetBenefitSelectionExplainQuery(accountId || "", {
    skip: !accountId || !explainOpen || dirty,
  });

  const {
    data: history,
    isFetching: historyLoading,
    isError: historyError,
  } = useGetBenefitSelectionHistoryQuery(accountId || "", {
    skip: !accountId || !historyOpen,
  });

  // The card seeds itself once per response and then owns its own edits — re-seeding on every render
  // would discard what somebody is in the middle of typing.
  useEffect(() => {
    if (view) setForm(toForm(view));
  }, [view]);

  // Debounced so that dragging an exception or typing a keyword does not fire a request per
  // keystroke; the panel is open while this runs, so it shows the previous answer until this lands.
  useEffect(() => {
    if (!accountId || !explainOpen || !dirty || !form?.serviceLine)
      return undefined;
    setDraftExplainLoading(true);
    const timer = setTimeout(() => {
      explainDraft({ accountId, body: toSaveRequest(form) })
        .unwrap()
        .then((result) => setDraftExplanation(result.data))
        // A failure leaves the last answer on screen rather than blanking the panel; the header
        // still says the form is unsaved, so nothing here can be mistaken for what is in force.
        .catch(() => undefined)
        .finally(() => setDraftExplainLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [accountId, explainOpen, dirty, form, explainDraft]);

  const payerOptions = useMemo(
    () =>
      sortBy(
        payerByAccount?.data?.map((payer: any) => ({
          label: payer.name,
          value: payer.id,
        })) || [],
        ["label"]
      ),
    [payerByAccount]
  );

  const vocabularyRows = vocabulary?.data?.rows ?? [];

  const knownRoles = useMemo(() => {
    const roles = new Set<string>(vocabularyRows.map((row) => row.role));
    (view?.vocabularyRolesInUse ?? []).forEach((entry) =>
      roles.add(entry.role)
    );
    (vocabulary?.data?.rolesInUseWithoutGlobalSpelling ?? []).forEach((gap) =>
      roles.add(gap.role)
    );
    return [...roles].sort();
  }, [vocabulary, view]);

  if (!accountId) {
    return (
      <span className="text-sm text-color-secondary">
        Benefit selection can be configured once the account has been created
        and its payers are known.
      </span>
    );
  }

  if (isLoading || !form || !view) {
    if (isError) {
      return (
        <span
          className="text-sm text-red-600"
          data-testid="benefitSelection.loadError"
        >
          This account&apos;s benefit selection could not be loaded.
        </span>
      );
    }
    return <LoadingSpinner />;
  }

  const emptyFilterIndex = form.filters.findIndex(
    (rule: any) => !filterStatesSomething(rule)
  );
  /**
   * CLE-2802 — with `BENEFIT_SELECTION_RULES=ON` the API shows "2 · Selection rules" in place of
   * steps 2 and 3. The old steps still ship for OFF and SHADOW until the flag is on everywhere.
   */
  const rulesOn = form.selectionRules?.mode === "ON";
  const canSave =
    canEdit &&
    Boolean(form.serviceLine) &&
    // The payer filters are not on screen while the rules step is, so an empty one cannot be fixed.
    (rulesOn || emptyFilterIndex === -1) &&
    selectionRulesAreValid(form.selectionRules);

  const buildRequest = () => toSaveRequest(form);

  const handleSave = async () => {
    try {
      await saveBenefitSelection({
        accountId,
        body: buildRequest(),
      }).unwrap();
      setPreviewOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Benefit selection saved",
        detail: "New estimates on this account use these settings.",
      });
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Not saved",
        detail:
          error?.data?.message ||
          error?.message ||
          "The benefit selection could not be saved.",
      });
    }
  };

  const handleEngine = async (enabled: boolean) => {
    try {
      await setEngineEnabled({ accountId, enabled }).unwrap();
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Not changed",
        detail:
          error?.data?.message ||
          error?.message ||
          "The engine switch could not be changed.",
      });
    }
  };

  const handlePreview = async () => {
    setPreviewResult(undefined);
    setPreviewStatus("idle");
    setPreviewOpen(true);
    try {
      const result = await runPreview({
        accountId,
        body: buildRequest(),
      }).unwrap();
      setPreviewResult(result.data);
    } catch (error: any) {
      // A 404 here is the expected shape until PR 3 lands, and it is reported as "not available yet"
      // rather than as a failure — the two are different facts and only one is worth a red banner.
      setPreviewStatus(error?.status === 404 ? "unavailable" : "error");
    }
  };

  const openExplain = (benefit: string | null) => {
    setExplainBenefit(benefit);
    setExplainOpen(true);
  };

  const setLineSelection = (
    benefit: string,
    selection: IBenefitLineSelection | null
  ) =>
    setForm((current) =>
      current
        ? {
            ...current,
            lineSelection: { ...current.lineSelection, [benefit]: selection },
          }
        : current
    );

  return (
    <Surface
      className="flex flex-column gap-4"
      data-testid="benefitSelection.card"
    >
      <div className="flex align-items-start justify-content-between gap-3">
        <div className="flex flex-column">
          <CardTitle>New engine benefit selection configuration</CardTitle>
        </div>
        <Button
          type="button"
          label="History"
          className="p-button-text p-button-sm"
          data-testid="benefitSelection.historyLink"
          onClick={() => setHistoryOpen(true)}
        />
      </div>

      <EngineStatusLine
        engine={view.engine}
        serviceLineChosen={Boolean(form.serviceLine)}
        canEdit={canEdit}
        saving={switching}
        onSetEnabled={handleEngine}
      />

      {/*
        The steps are ordered as somebody CONFIGURES an account, not as the engine executes it: the
        service line is the decision the other two are relative to, and a filter or a selector read
        before it has nothing to be about. The filters' own sentence says when they run, because
        that is a fact about them rather than about where they sit. All three open, each collapsible
        on its own.
      */}
      {/*
        FOUR SLOTS, ALL OPEN: React counts a `false` child as a position, so with the old steps
        hidden (or the rules step hidden) the visible tabs sit at indices 0, 1 or 0, 2, 3.
      */}
      <Accordion multiple activeIndex={[0, 1, 2, 3]}>
        <AccordionTab
          header={<StepHeading>1 · Service line</StepHeading>}
          data-testid="benefitSelection.step1"
        >
          <Section>
            <span className="text-sm text-color-secondary">
              Which of the payer&apos;s benefit service lines this
              account&apos;s copay and co-insurance are read from.
            </span>

            {form.serviceLine === null && (
              <span
                className="text-sm text-orange-700"
                data-testid="benefitSelection.noServiceLine"
              >
                No service line is set for this account. Choose the one this
                facility bills under — without it the engine answers no copay,
                no co-insurance and no physician copay.
              </span>
            )}

            <ServiceLinePicker
              value={form.serviceLine}
              disabled={!canEdit}
              onChange={(serviceLine: BenefitServiceLine) =>
                setForm({ ...form, serviceLine })
              }
            />

            {/*
              Under the picker rather than beside it: the tier is read INSIDE the service line
              chosen above, so it is a second question about that choice and not a sixth thing to
              choose between.
            */}
            <TierPreferenceControl
              id="benefitSelection.tierPreference"
              value={form.tierPreference}
              disabled={!canEdit}
              onChange={(tierPreference: BenefitTierPreference) =>
                setForm({ ...form, tierPreference })
              }
            />
          </Section>

          <Section>
            <PhysicianCopayControl
              billed={form.physicianCopayBilled}
              disabled={!canEdit}
              onChange={(physicianCopayBilled) =>
                setForm({ ...form, physicianCopayBilled })
              }
            />
          </Section>

          <Section>
            <ExceptionsList
              exceptions={form.exceptions}
              accountServiceLine={form.serviceLine}
              accountTierPreference={form.tierPreference}
              payerOptions={payerOptions}
              loadingPayers={loadingPayers}
              onChange={(exceptions) => setForm({ ...form, exceptions })}
            />
          </Section>
        </AccordionTab>

        {rulesOn && form.selectionRules && (
          <AccordionTab
            header={<StepHeading>2 · Selection rules</StepHeading>}
            data-testid="benefitSelection.stepRules"
          >
            <Section>
              <SelectionRulesStep
                accountId={accountId}
                value={form.selectionRules}
                physicianCopayBilled={form.physicianCopayBilled}
                canEdit={canEdit}
                payerOptions={payerOptions}
                loadingPayers={loadingPayers}
                knownRoles={knownRoles}
                onChange={(selectionRules) =>
                  setForm({ ...form, selectionRules })
                }
              />
            </Section>
          </AccordionTab>
        )}

        {!rulesOn && (
          <AccordionTab
            header={<StepHeading>2 · Filter the response</StepHeading>}
            data-testid="benefitSelection.step2"
          >
            <Section>
              <span className="text-sm text-color-secondary">
                Runs first, for every benefit: drops lines we never use and
                prefers the ones we do.
              </span>
              <span
                className="text-sm text-color-secondary"
                data-testid="benefitSelection.filtersScope"
              >
                These filters apply to the benefit engine only.
              </span>

              <BenefitSelectionRules
                showHeading={false}
                value={form.filters}
                payerOptions={payerOptions}
                loadingPayers={loadingPayers}
                onChange={(filters: IBenefitSelectionRule[]) =>
                  setForm({ ...form, filters })
                }
              />

              {emptyFilterIndex >= 0 && (
                <span
                  className="text-sm text-red-600"
                  data-testid="benefitSelection.emptyFilter"
                >
                  {`Filter ${
                    emptyFilterIndex + 1
                  } narrows nothing — no payers, plans, keywords or network status. Give it something to match, or remove it.`}
                </span>
              )}
            </Section>
          </AccordionTab>
        )}

        {!rulesOn && (
          <AccordionTab
            header={<StepHeading>3 · Line priority</StepHeading>}
            data-testid="benefitSelection.step3"
          >
            <Section>
              <span className="text-sm text-color-secondary">
                For each benefit, selectors are tried in order and the first
                that finds a line on the step 1 service line is used.
              </span>

              <LineSelectionTable
                lineSelection={form.lineSelection}
                lineSelectionDefaults={view.lineSelectionDefaults}
                physicianCopayBilled={form.physicianCopayBilled}
                vocabulary={vocabularyRows}
                onEdit={setEditingLine}
              />
            </Section>
          </AccordionTab>
        )}
      </Accordion>

      {/*
        The summary is the result of ALL THREE steps, so it sits after them rather than inside the
        service-line step, where it read as one more thing that choice decided.
      */}
      <div className="flex flex-column gap-2">
        <StepHeading>Summary — how each benefit is selected</StepHeading>
        <WhatThisAccountReads
          serviceLine={form.serviceLine}
          physicianCopayBilled={form.physicianCopayBilled}
          tierPreference={form.tierPreference}
          onExplain={openExplain}
        />
      </div>

      <Divider />

      <div className="flex align-items-center justify-content-end gap-3">
        {!canEdit && (
          <span className="text-sm text-color-secondary">
            Read only — changing this needs super admin.
          </span>
        )}
        <Button
          type="button"
          label="Preview"
          className="p-button-outlined p-button-sm"
          loading={previewing}
          disabled={!canSave}
          data-testid="benefitSelection.preview"
          onClick={handlePreview}
        />
        <Button
          className="p-button-sm"
          type="button"
          label="Save benefit configuration"
          loading={saving}
          disabled={!canSave}
          data-testid="benefitSelection.save"
          onClick={handleSave}
        />
      </div>

      {CONFIGURABLE_BENEFITS.map((benefit) => (
        <LineSelectionDialog
          key={benefit}
          visible={editingLine === benefit}
          benefit={benefit}
          serviceLineLabel={serviceLineLabel(form.serviceLine)}
          value={form.lineSelection[benefit] ?? null}
          defaultSelection={
            view.lineSelectionDefaults[benefit] ?? {
              selectors: [{ any: true }],
              tieBreak: "HIGHEST",
            }
          }
          knownRoles={knownRoles}
          vocabulary={vocabularyRows}
          onSave={(selection) => {
            setLineSelection(benefit, selection);
            setEditingLine(null);
          }}
          onHide={() => setEditingLine(null)}
        />
      ))}

      <ExplainDrawer
        visible={explainOpen}
        onHide={() => setExplainOpen(false)}
        explanation={dirty ? draftExplanation : explanation?.data}
        benefit={explainBenefit}
        isLoading={dirty ? draftExplainLoading : savedExplainLoading}
        isError={!dirty && explainError}
        accountId={accountId}
        // 🚨 SENT ONLY WHILE THE FORM DIFFERS FROM WHAT IS SAVED, and the same body the Save would
        // send. A check explained against the screen's edits has to be explained against ALL of
        // them; sending the form unconditionally would make every answer read "using your unsaved
        // changes" on an account nobody has touched.
        configuration={dirty ? toSaveRequest(form) : undefined}
      />

      <HistoryDrawer
        visible={historyOpen}
        onHide={() => setHistoryOpen(false)}
        changes={history?.data?.changes ?? []}
        isLoading={historyLoading}
        isError={historyError}
      />

      <PreviewDialog
        visible={previewOpen}
        onHide={() => setPreviewOpen(false)}
        result={previewResult}
        isLoading={previewing}
        isUnavailable={previewStatus === "unavailable"}
        isError={previewStatus === "error"}
        saving={saving}
        onSave={handleSave}
      />
    </Surface>
  );
}
